import { useId, useState, type ReactNode } from 'react'
import { Icon } from '../ui'
import './flow.css'

// A fold: small optional detail on a page, closed until somebody opens it
// (release 3.3, task F2 of the 7 Oct plan, rule 2.4).
//
// # WHY THIS EXISTS
//
// Optional fields stacked on a form make it twice as tall for everybody who
// does not need them (Add a vendor was 18 labelled fields on one page). A
// fold keeps them one press away. Two levels at most: what is on the page and
// what opens from it, never a fold inside a fold.
//
// # WHAT A CLOSED FOLD MUST SAY
//
// A hidden setting causes mistakes, so a fold that holds values says so while
// closed ("3 set", or the values themselves when there are one or two: see
// flow.ts, foldSummary). A fold that is closed never looks the same as one that
// is empty.
//
// # WHAT IT KEEPS
//
// What is inside stays mounted while closed (hidden, not removed), so a field
// typed into and folded away keeps its value and is still sent with the form.
// With `remember`, the open or closed state is kept per person in this
// browser, because a person who always opens the fold should not have to
// open it every time.

function readOpen(key: string | undefined, fallback: boolean): boolean {
  if (!key) return fallback
  try {
    const v = localStorage.getItem('tprm.fold.' + key)
    return v === null ? fallback : v === '1'
  } catch {
    return fallback
  }
}

function writeOpen(key: string | undefined, open: boolean): void {
  if (!key) return
  try {
    localStorage.setItem('tprm.fold.' + key, open ? '1' : '0')
  } catch {
    // Storage refused: the fold still opens and closes, it is just not kept.
  }
}

export function Disclosure({
  title,
  summary,
  children,
  open,
  onToggle,
  defaultOpen = false,
  remember,
  actionId,
}: {
  title: ReactNode
  // Said beside the title while the fold is closed: what is inside.
  summary?: ReactNode
  children: ReactNode
  // Held by the screen, when the screen must open it itself (a field inside
  // that is wrong when the form is sent).
  open?: boolean
  onToggle?: (open: boolean) => void
  defaultOpen?: boolean
  // A key to keep the state under, per person in this browser.
  remember?: string
  // The persona walk's hook.
  actionId?: string
}) {
  const id = useId()
  const [own, setOwn] = useState(() => readOpen(remember, defaultOpen))
  const isOpen = open ?? own
  const toggle = () => {
    const next = !isOpen
    if (open === undefined) setOwn(next)
    writeOpen(remember, next)
    onToggle?.(next)
  }
  const openForInvalid = () => {
    if (isOpen) return
    if (open === undefined) setOwn(true)
    onToggle?.(true)
  }
  return (
    <div className={'kit-fold' + (isOpen ? ' kit-fold-open' : '')}>
      <button
        type="button"
        className="kit-fold-head"
        aria-expanded={isOpen}
        aria-controls={id + 'body'}
        data-action={actionId}
        onClick={toggle}
      >
        <span className="kit-fold-chev" aria-hidden="true">
          <Icon name="chevron" size={14} />
        </span>
        <span className="kit-fold-title">{title}</span>
        {!isOpen && summary != null && summary !== '' && <span className="kit-fold-summary">{summary}</span>}
      </button>
      {/* A field inside that the browser finds wrong opens the fold, so the
          form never refuses to send over a field nobody can see (review of
          12 Oct, S3). React hears "invalid" in the capture phase. */}
      <div className="kit-fold-body" id={id + 'body'} hidden={!isOpen} onInvalidCapture={openForInvalid}>
        {children}
      </div>
    </div>
  )
}
